/**
 * Fullscreen Product Image Modal Component
 * Opens any product image full screen in the browser when clicked.
 */
class FullscreenImageModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.images = [];
    this.currentIndex = 0;
    this.isZoomed = false;
  }

  connectedCallback() {
    this.dialog = this.querySelector('.fullscreen-modal-dialog');
    this.imageElement = this.querySelector('.fullscreen-modal-image');
    this.counterElement = this.querySelector('.fullscreen-modal-counter');
    this.prevButton = this.querySelector('.fullscreen-modal-prev');
    this.nextButton = this.querySelector('.fullscreen-modal-next');
    this.closeButton = this.querySelector('.fullscreen-modal-close');
    this.zoomButton = this.querySelector('.fullscreen-modal-zoom-toggle');
    this.imageWrapper = this.querySelector('.fullscreen-modal-image-wrapper');
    this.backdrop = this.querySelector('.fullscreen-modal-backdrop');

    this.bindEvents();
    this.attachProductImageListeners();
    this.observeDomChanges();
  }

  bindEvents() {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.backdrop) {
      this.backdrop.addEventListener('click', (e) => {
        if (e.target === this.backdrop || e.target === this.imageWrapper) {
          this.close();
        }
      });
    }

    if (this.prevButton) {
      this.prevButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.showPrev();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.showNext();
      });
    }

    if (this.zoomButton) {
      this.zoomButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleZoom();
      });
    }

    if (this.imageElement) {
      this.imageElement.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleZoom();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;

      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'ArrowLeft') {
        this.showPrev();
      } else if (e.key === 'ArrowRight') {
        this.showNext();
      }
    });

    // Touch swipe support
    let touchStartX = 0;
    let touchEndX = 0;

    this.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      if (this.isZoomed) return;
      touchEndX = e.changedTouches[0].screenX;
      if (touchStartX - touchEndX > 50) {
        this.showNext();
      } else if (touchEndX - touchStartX > 50) {
        this.showPrev();
      }
    }, { passive: true });
  }

  observeDomChanges() {
    const observer = new MutationObserver(() => {
      this.attachProductImageListeners();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  getHighResUrl(img) {
    if (!img) return '';
    const maxRes = img.getAttribute('data_max_resolution') || img.getAttribute('data-max-resolution') || img.getAttribute('data-zoom-src') || img.getAttribute('data-src');
    if (maxRes) return maxRes;

    const currentSrc = img.currentSrc || img.src || '';
    if (currentSrc) {
      return currentSrc.replace(/width=\d+/, 'width=2400').replace(/_\d+x\d*/, '_2400x');
    }
    return '';
  }

  collectImages(clickedImg) {
    const container = clickedImg.closest('media-gallery, .media-gallery, .product-media-gallery, [data-section-type="product"], .product-information, .product-thumbnail-wrapper, .slider--product__holder, [data-bstr-ppslider], section, main') || document.body;
    const imgList = Array.from(container.querySelectorAll('.product-media__image, .product-media img, .media-gallery img, .product-single__media img, [data-product-image] img, .product-media-container img, .slide--product img, [data-bstr-slide] img, img[data-zoomable]'));

    const uniqueMap = new Map();
    imgList.forEach((img) => {
      const src = this.getHighResUrl(img);
      if (src && !uniqueMap.has(src) && !img.closest('.fullscreen-image-modal')) {
        uniqueMap.set(src, {
          src: src,
          alt: img.alt || 'Product Image',
        });
      }
    });

    const images = Array.from(uniqueMap.values());
    if (images.length === 0) {
      const singleSrc = this.getHighResUrl(clickedImg);
      return [{ src: singleSrc, alt: clickedImg.alt || 'Product Image' }];
    }

    return images;
  }

  attachProductImageListeners() {
    const productImages = document.querySelectorAll(
      '.product-media__image, .product-media img, .media-gallery img, .product-single__media img, [data-product-image], .product-media-container img, .slide--product img, [data-bstr-slide] img, img[data-zoomable], .product-media-container__zoom-button'
    );

    productImages.forEach((element) => {
      if (element.dataset.fullscreenListenerAttached) return;
      element.dataset.fullscreenListenerAttached = 'true';

      element.style.cursor = 'zoom-in';

      element.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const img = element.tagName === 'IMG' ? element : element.querySelector('img') || element.closest('.product-media-container, .slide--product, [data-bstr-slide], li, div')?.querySelector('img');
        if (!img) return;

        const images = this.collectImages(img);
        const targetSrc = this.getHighResUrl(img);
        let activeIndex = images.findIndex((item) => item.src === targetSrc);
        if (activeIndex === -1) activeIndex = 0;

        this.open(images, activeIndex);
      });
    });
  }

  open(images, index = 0) {
    if (!images || images.length === 0) return;
    this.images = images;
    this.currentIndex = index;
    this.isOpen = true;
    this.isZoomed = false;

    this.updateImage();
    this.classList.add('is-active');
    document.body.classList.add('fullscreen-modal-open');
    document.documentElement.style.overflow = 'hidden';

    if (this.dialog && typeof this.dialog.showModal === 'function' && !this.dialog.open) {
      this.dialog.showModal();
    }
  }

  close() {
    this.isOpen = false;
    this.isZoomed = false;
    this.classList.remove('is-active');
    document.body.classList.remove('fullscreen-modal-open');
    document.documentElement.style.overflow = '';

    if (this.imageElement) {
      this.imageElement.classList.remove('is-zoomed');
    }

    if (this.dialog && this.dialog.open) {
      this.dialog.close();
    }
  }

  showPrev() {
    if (this.images.length <= 1) return;
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    this.isZoomed = false;
    this.updateImage();
  }

  showNext() {
    if (this.images.length <= 1) return;
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.isZoomed = false;
    this.updateImage();
  }

  toggleZoom() {
    this.isZoomed = !this.isZoomed;
    if (this.imageElement) {
      this.imageElement.classList.toggle('is-zoomed', this.isZoomed);
    }
    if (this.zoomButton) {
      this.zoomButton.setAttribute('aria-pressed', this.isZoomed ? 'true' : 'false');
      this.zoomButton.classList.toggle('is-active', this.isZoomed);
    }
  }

  updateImage() {
    const current = this.images[this.currentIndex];
    if (!current || !this.imageElement) return;

    this.imageElement.classList.remove('is-zoomed');
    if (this.zoomButton) {
      this.zoomButton.setAttribute('aria-pressed', 'false');
      this.zoomButton.classList.remove('is-active');
    }

    this.imageElement.src = current.src;
    this.imageElement.alt = current.alt || 'Product Image';

    if (this.counterElement) {
      this.counterElement.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
      this.counterElement.style.display = this.images.length > 1 ? 'block' : 'none';
    }

    if (this.prevButton) {
      this.prevButton.style.display = this.images.length > 1 ? 'flex' : 'none';
    }
    if (this.nextButton) {
      this.nextButton.style.display = this.images.length > 1 ? 'flex' : 'none';
    }
  }
}

if (!customElements.get('fullscreen-image-modal')) {
  customElements.define('fullscreen-image-modal', FullscreenImageModal);
}
